import React from 'react';
import IconButton from '@mui/material/IconButton';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useAlert, useFetch, useToggle } from '../../hooks';
import { useAuth } from '../../context';
import {
    Field,
    Form,
    FormType,
    InputEventProps,
    ActionButton,
    Alert
} from '..';

export interface loginValuesProps {
    email: string;
    password: string;
}

const loginInitialValues: loginValuesProps = {
    email: '',
    password: ''
};
export const LoginForm: React.FC = React.memo(() => {
    const [post, loading] = useFetch('/utLogin', 'POST');
    const { alert, dispatchAlert, closeAlert } = useAlert();
    const { checkValidSession, dispatchAuth } = useAuth();
    const [loginValues, setLoginValues] =
        React.useState<loginValuesProps>(loginInitialValues);
    const [showPassword, togglePassword] = useToggle(false);

    const onChangeHandler = React.useCallback(
        (key: keyof loginValuesProps) => (e: InputEventProps) =>
            setLoginValues(prevLoginValues => {
                return {
                    ...prevLoginValues,
                    [key]: e.target.value
                };
            }),
        []
    );

    const errorHandler = React.useCallback(
        (message: string) => {
            dispatchAlert({
                type: 'UPDATE_ALERT',
                message:
                    message === 'Access denied'
                        ? 'Incorrect email or password'
                        : message,
                severity: 'error'
            });
            setLoginValues(loginInitialValues);
            dispatchAuth({ type: 'RESET_SESSION' });
        },
        [dispatchAlert, dispatchAuth]
    );

    const loginHandler = React.useCallback(
        async (e: FormType) => {
            e.preventDefault();
            dispatchAlert({
                type: 'UPDATE_ALERT',
                message: 'Login success!',
                severity: 'success'
            });
            const result = await post({ data: loginValues });
            result.success
                ? checkValidSession(result.data, 'login')
                : errorHandler(result.msg);
            dispatchAlert({ type: 'OPEN' });
        },
        [dispatchAlert, post, loginValues, errorHandler, checkValidSession]
    );

    return (
        <Form
            sx={{
                maxWidth: '40rem',
                width: '100%',
                margin: '10rem auto'
            }}
            onSubmit={loginHandler}
        >
            <Alert closeHandler={closeAlert} {...alert} />
            <Field
                name='Username'
                onChange={onChangeHandler('email')}
                autoFocus
            />
            <Field
                name='Password'
                onChange={onChangeHandler('password')}
                type={showPassword ? 'text' : 'password'}
                endAdornment={
                    <IconButton onClick={togglePassword}>
                        {showPassword ? (
                            <VisibilityIcon />
                        ) : (
                            <VisibilityOffIcon />
                        )}
                    </IconButton>
                }
            />
            <ActionButton type='submit' title='Login' loading={loading} grid />
        </Form>
    );
});
